import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Area, AreaChart, ComposedChart, Bar, ReferenceLine, Brush
} from 'recharts';
import { ArrowUp, ArrowDown, Download, Clock, Calendar, BarChart2, TrendingUp, DollarSign } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'bootstrap/dist/css/bootstrap.min.css';
const baseUrl = process.env.REACT_APP_SERVER_URL;

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const timestamp = new Date(label);
    const marketValue = payload.find(p => p.name === "Market Value")?.value || 0;
    const investment = payload.find(p => p.name === "Investment")?.value || 0;
    const gainPercent = payload.find(p => p.name === "% Gain")?.value || 0;
    const gainValue = marketValue - investment;
    const isProfit = gainValue >= 0;

    return (
      <div className="card shadow">
        <div className="card-body p-3">
          <h6 className="fw-bold text-secondary mb-2">
            {timestamp.toLocaleDateString()} {timestamp.toLocaleTimeString()}
          </h6>
          <div className="row g-2">
            <div className="col-6">
              <small className="text-muted d-block">Market Value:</small>
              <span className="fw-semibold text-primary">₹{marketValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Investment:</small>
              <span className="fw-semibold text-success">₹{investment.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Gain/Loss (₹):</small>
              <span className={`fw-semibold ${isProfit ? 'text-success' : 'text-danger'}`}>
                {isProfit ? '+' : ''}₹{gainValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Gain/Loss (%):</small>
              <span className={`fw-semibold ${gainPercent >= 0 ? 'text-success' : 'text-danger'}`}>
                {gainPercent >= 0 ? '+' : ''}{gainPercent.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const SummaryCard = ({ title, value, change, timestamp, icon, borderColor }) => {
  // Handle cases where change might be undefined
  const safeChange = change !== undefined && change !== null ? change : 0;
  const isPositive = safeChange >= 0;
  
  return (
    <div className={`card h-100 border-start border-4 ${borderColor}`}>
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="text-muted small mb-1">{title}</h6>
            <h4 className="fw-bold mb-2">₹{(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</h4>
          </div>
          <div className={`d-flex align-items-center justify-content-center rounded-circle p-3 ${isPositive ? 'bg-success bg-opacity-10' : 'bg-danger bg-opacity-10'}`}>
            {icon}
          </div>
        </div>
        <div className="d-flex align-items-center mt-2">
          <span className={`small fw-semibold ${isPositive ? 'text-success' : 'text-danger'}`}>
            {isPositive ? '+' : ''}{safeChange.toFixed(2)}%
          </span>
          {timestamp && 
            <small className="text-muted ms-2">{timestamp.toLocaleDateString()}</small>
          }
          {!timestamp && 
            <small className="text-muted ms-2">from investment</small>
          }
        </div>
      </div>
    </div>
  );
};

const PortfolioChart = () => {
  const [data, setData] = useState([]);
  const [showPercent, setShowPercent] = useState(true);
  const [viewMode, setViewMode] = useState('minute'); // 'minute', 'hour', 'day'
  const [chartType, setChartType] = useState('line'); // 'line', 'area', 'composed'
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    current: { value: 0, change: 0 },
    highest: { value: 0, timestamp: null },
    lowest: { value: 0, timestamp: null }
  });
  const chartRef = useRef();
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/portfolio/history?mode=${viewMode}`);
      const processedData = response.data.map(item => ({
        ...item,
        gain_value: item.total_market_value - item.total_investment,
        timestamp: new Date(item.timestamp).getTime() // Convert to timestamp for easier handling
      }));
      
      setData(processedData);
      
      // Calculate summary data
      if (processedData.length > 0) {
        const current = processedData[processedData.length - 1];
        const highest = processedData.reduce((max, item) => 
          item.total_market_value > max.total_market_value ? item : max, processedData[0]);
        const lowest = processedData.reduce((min, item) => 
          item.total_market_value < min.total_market_value ? item : min, processedData[0]);
        
        // Ensure all values exist and are numeric
        const currentValue = current?.total_market_value || 0;
        const currentChange = current?.gain_percent || 0;
        const currentGainValue = current?.gain_value || 0;
        const highestValue = highest?.total_market_value || 0;
        const highestChange = highest?.gain_percent || 0;
        const lowestValue = lowest?.total_market_value || 0;
        const lowestChange = lowest?.gain_percent || 0;
        
        setSummary({
          current: { 
            value: currentValue, 
            change: currentChange,
            gainValue: currentGainValue
          },
          highest: { 
            value: highestValue, 
            timestamp: highest?.timestamp ? new Date(highest.timestamp) : null,
            percent: highestChange
          },
          lowest: { 
            value: lowestValue, 
            timestamp: lowest?.timestamp ? new Date(lowest.timestamp) : null,
            percent: lowestChange
          }
        });
      }
    } catch (error) {
      console.error("Failed to fetch portfolio history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    const interval = setInterval(fetchHistory, 15000);
    return () => clearInterval(interval);
  }, [viewMode]);

  const exportChart = async (format) => {
    const element = chartRef.current;
    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL('image/png');

    if (format === 'png') {
      const link = document.createElement('a');
      link.download = `portfolio_chart_${new Date().toISOString().split('T')[0]}.png`;
      link.href = imgData;
      link.click();
    } else if (format === 'pdf') {
      const pdf = new jsPDF();
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`portfolio_chart_${new Date().toISOString().split('T')[0]}.pdf`);
    }
  };

  const renderTimeFrameButton = (mode, label, icon) => (
    <button
      onClick={() => setViewMode(mode)}
      className={`btn btn-sm ${
        viewMode === mode 
          ? 'btn-primary' 
          : 'btn-outline-secondary'
      } d-flex align-items-center`}
    >
      {icon}
      <span className="ms-1">{label}</span>
    </button>
  );

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    
    switch(viewMode) {
      case 'minute':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case 'hour':
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case 'day':
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      default:
        return date.toLocaleTimeString();
    }
  };

  const getChartColor = () => {
    if (data.length === 0) return { stroke: "#0d6efd", fill: "#0d6efd" };
    
    const latestData = data[data.length - 1];
    const isPositive = latestData.gain_percent >= 0;
    
    return { 
      stroke: isPositive ? "#198754" : "#dc3545",
      fill: isPositive ? "rgba(25, 135, 84, 0.1)" : "rgba(220, 53, 69, 0.1)"
    };
  };

  const renderChart = () => {
    const chartColor = getChartColor();
    
    if (loading) {
      return (
        <div className="d-flex align-items-center justify-content-center bg-light" style={{height: "450px"}}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading portfolio data...</p>
          </div>
        </div>
      );
    }

    switch(chartType) {
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={450}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={chartColor.stroke} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={chartColor.stroke} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={formatTime}
                stroke="#6c757d"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left" 
                stroke="#6c757d"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`}
              />
              {showPercent && (
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  stroke="#6c757d"
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine y={0} stroke="#6c757d" yAxisId={showPercent ? "right" : "left"} />
              <Area 
                yAxisId="left" 
                type="monotone" 
                dataKey="total_market_value" 
                stroke={chartColor.stroke} 
                fillOpacity={1}
                fill="url(#colorValue)" 
                name="Market Value" 
              />
              {showPercent ? (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="gain_percent"
                  stroke="#0d6efd"
                  strokeWidth={2}
                  name="% Gain"
                  dot={false}
                />
              ) : (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="gain_value"
                  stroke="#0d6efd"
                  strokeWidth={2}
                  name="₹ Gain"
                  dot={false}
                />
              )}
              <Brush dataKey="timestamp" height={30} stroke="#0d6efd" tickFormatter={formatTime} />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'composed':
        return (
          <ResponsiveContainer width="100%" height={450}>
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
              <XAxis 
                dataKey="timestamp" 
                tickFormatter={formatTime}
                stroke="#6c757d"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left" 
                stroke="#6c757d" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`}
              />
              {showPercent && (
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  stroke="#6c757d"
                  tickFormatter={(value) => `${value}%`}
                  tick={{ fontSize: 12 }}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine y={0} stroke="#6c757d" yAxisId={showPercent ? "right" : "left"} />
              <Area 
                yAxisId="left" 
                type="monotone" 
                dataKey="total_market_value" 
                fill={chartColor.fill} 
                stroke={chartColor.stroke} 
                name="Market Value" 
              />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="total_investment" 
                stroke="#0d6efd" 
                strokeDasharray="5 5"
                strokeWidth={2}
                dot={false}
                name="Investment" 
              />
              {showPercent ? (
                <Bar
                  yAxisId="right"
                  dataKey="gain_percent"
                  fill={(entry) => (entry.gain_percent >= 0 ? "#198754" : "#dc3545")}
                  name="% Gain"
                  barSize={20}
                />
              ) : (
                <Bar
                  yAxisId="left"
                  dataKey="gain_value"
                  fill={(entry) => (entry.gain_value >= 0 ? "#198754" : "#dc3545")}
                  name="₹ Gain"
                  barSize={20}
                />
              )}
              <Brush dataKey="timestamp" height={30} stroke="#0d6efd" tickFormatter={formatTime} />
            </ComposedChart>
          </ResponsiveContainer>
        );
      
      default: // line chart
        return (
          <ResponsiveContainer width="100%" height={450}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
              <XAxis 
                dataKey="timestamp"
                tickFormatter={formatTime}
                stroke="#6c757d"
                tick={{ fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left" 
                stroke="#6c757d" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `₹${(value/1000).toFixed(0)}K`}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                stroke="#6c757d" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => (showPercent ? `${value}%` : `₹${(value/1000).toFixed(0)}K`)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <ReferenceLine y={0} stroke="#6c757d" yAxisId="right" />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="total_market_value" 
                stroke="#0d6efd" 
                strokeWidth={2}
                name="Market Value"
                dot={{ r: 1 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                yAxisId="left" 
                type="monotone" 
                dataKey="total_investment" 
                stroke="#198754" 
                strokeWidth={2}
                name="Investment"
                dot={{ r: 1 }}
                activeDot={{ r: 6 }}
              />
              {showPercent ? (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="gain_percent"
                  stroke={chartColor.stroke}
                  strokeWidth={2}
                  name="% Gain"
                  dot={false}
                />
              ) : (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="gain_value"
                  stroke={chartColor.stroke}
                  strokeWidth={2}
                  name="₹ Gain"
                  dot={false}
                />
              )}
              <Brush dataKey="timestamp" height={30} stroke="#0d6efd" tickFormatter={formatTime} />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="container-fluid p-4">
      <div className="card shadow-lg border-0">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
            <h2 className="h4 fw-bold mb-0">
              <i className="bi bi-graph-up me-2"></i>
              Portfolio Performance
            </h2>
          </div>

          {/* Summary Cards - Single Line */}
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <SummaryCard 
                title="Current Value" 
                value={summary.current?.value || 0} 
                change={summary.current?.change || 0}
                icon={<BarChart2 size={20} className={(summary.current?.change || 0) >= 0 ? 'text-success' : 'text-danger'} />}
                borderColor="border-primary"
              />
            </div>
            
            <div className="col-md-4">
              <SummaryCard 
                title="Highest Value" 
                value={summary.highest?.value || 0} 
                change={summary.highest?.percent || 0}
                timestamp={summary.highest?.timestamp}
                icon={<ArrowUp size={20} className="text-success" />}
                borderColor="border-success"
              />
            </div>
            
            <div className="col-md-4">
              <SummaryCard 
                title="Lowest Value" 
                value={summary.lowest?.value || 0} 
                change={summary.lowest?.percent || 0}
                timestamp={summary.lowest?.timestamp}
                icon={<ArrowDown size={20} className="text-danger" />}
                borderColor="border-danger"
              />
            </div>
          </div>

          {/* Controls Bar */}
          <div className="card mb-4 border">
            <div className="card-body py-2">
              <div className="row align-items-center">
                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <span className="fw-medium me-2 text-secondary">Chart Type:</span>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        onClick={() => setChartType('line')}
                        className={`btn ${chartType === 'line' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        <TrendingUp size={14} className="me-1" />
                        Line
                      </button>
                      <button
                        onClick={() => setChartType('area')}
                        className={`btn ${chartType === 'area' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        <BarChart2 size={14} className="me-1" />
                        Area
                      </button>
                      <button
                        onClick={() => setChartType('composed')}
                        className={`btn ${chartType === 'composed' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      >
                        <BarChart2 size={14} className="me-1" />
                        Combined
                      </button>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mb-3 mb-md-0">
                  <div className="d-flex align-items-center">
                    <span className="fw-medium me-2 text-secondary">Timeframe:</span>
                    <div className="btn-group btn-group-sm" role="group">
                      {renderTimeFrameButton('minute', '30m', <Clock size={14} className="me-1" />)}
                      {renderTimeFrameButton('hour', '24h', <Clock size={14} className="me-1" />)}
                      {renderTimeFrameButton('day', '7d', <Calendar size={14} className="me-1" />)}
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="d-flex align-items-center justify-content-md-end">
                    <button 
                      onClick={() => setShowPercent(!showPercent)} 
                      className={`btn btn-sm me-2 ${showPercent ? 'btn-primary' : 'btn-outline-secondary'}`}
                    >
                      <DollarSign size={14} className="me-1" />
                      {showPercent ? '% Gain' : '₹ Gain'}
                    </button>
                    
                    <div className="dropdown">
                      <button 
                        className="btn btn-sm btn-success d-flex align-items-center" 
                        type="button"
                        onClick={() => exportChart('png')}
                      >
                        <Download size={14} className="me-1" />
                        Export
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Chart */}
          <div 
            ref={chartRef} 
            className="card border mb-4"
          >
            <div className="card-body">
              {renderChart()}
            </div>
          </div>
          
          {/* Legend */}
          <div className="alert alert-info d-flex align-items-center" role="alert">
            <div className="me-2 bg-primary bg-opacity-10 p-2 rounded text-primary">
              <BarChart2 size={16} />
            </div>
            <div>
              <small>
                <span className="fw-medium">Chart Info:</span> Use the brush at the bottom to zoom. Toggle between chart types and time frames to analyze performance.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioChart;